import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import { z, ZodType } from 'zod';

import {
  adminAlertSchema,
  adminAmbassadeSchema,
  adminClientDetailSchema,
  adminClientSchema,
  adminSettingsSchema,
  ambassadeMeSchema,
  authResponseSchema,
  distributionSchema,
  formuleInfoSchema,
  formuleSchema,
  invitationSchema,
  menuSchema,
  monAmbassadeurSchema,
  ordersTodaySchema,
  orderSchema,
  paieSemaineSchema,
  paymentSchema,
  pwaMetricsSchema,
  refreshResponseSchema,
  remiseResultSchema,
  ticketJourSchema,
  revokeAllSessionsResponseSchema,
  sessionsResponseSchema,
  subscriptionSchema,
  voteCandidateSchema,
  voteStateSchema,
  type AdminAlertWire,
  type AdminAmbassadeWire,
  type AdminClientWire,
  type AmbassadeMeWire,
  type DistributionWire,
  type InvitationWire,
  type MenuWire,
  type MonAmbassadeurWire,
  type OrderWire,
  type OrdersTodayWire,
  type PaieSemaineWire,
  type PaymentWire,
  type PwaMetricsWire,
  type RemiseResultWire,
  type TicketJourWire,
  type SessionWire,
  type SubscriptionWire,
  type VoteCandidateWire,
  type VoteStateWire,
} from '@myfoodplus/shared';
import { useAuthStore } from '@/stores/authStore';
import { formatHeure } from '@/lib/utils';
import type { PwaTelemetryPayload } from './pwa';
import type {
  AdminAlert,
  AdminClient,
  AdminClientDetail,
  AdminOrdersToday,
  AuthResponse,
  Candidate,
  Formule,
  LoginPayload,
  Menu,
  MetricsOverview,
  Operateur,
  Order,
  OrderDeadlineRule,
  OrderHistoryItem,
  OrderStatus,
  Payment,
  RecoverPayload,
  RegisterPayload,
  Subscription,
  UpdateFormulePayload,
  VoteState,
} from './types';

// ─────────────────────────────────────────────
// Validation au bord réseau (ADR 0005 D3) : chaque adaptateur prioritaire
// (argent & identité — auth, payments, subscriptions, formules, + menus/
// votes/orders/admin-alerts) valide la réponse contre son schéma Zod
// partagé AVANT le mapping. Échec de schéma → erreur, jamais un cast
// silencieux : les pages routent vers leur état d'erreur honnête (D2).
// Endpoints non listés dans le brief Task 2 (menus/week, /candidates,
// admin/clients, admin/metrics/*, payments/confirm|pending|all, votes/vote|
// results, subscriptions history…) gardent le cast générique existant —
// migration incrémentale, cf. « règle d'avant » en tête de schemas.ts.
// ─────────────────────────────────────────────
function validateResponse<T>(schema: ZodType<T>, data: unknown, endpoint: string): T {
  const parsed = schema.safeParse(data);
  if (!parsed.success) {
    console.warn('[api] réponse inattendue', endpoint, parsed.error);
    throw new Error('Réponse serveur inattendue');
  }
  return parsed.data;
}

// ─────────────────────────────────────────────
// Discrimination des erreurs (Frappe 1, 2026-08-30) : les catch fourre-tout
// affichaient le même message pour une coupure réseau, un quota dépassé et
// une session expirée — jusqu'à un « mot de passe incorrect » trompeur sur
// un rejet de quota. Source unique de classification pour tous les écrans.
// ─────────────────────────────────────────────
export type ApiErrorKind = 'network' | 'rate' | 'auth' | 'server' | 'unknown';

export function classifyApiError(err: unknown): ApiErrorKind {
  if (axios.isAxiosError(err)) {
    if (!err.response) return 'network'; // coupure, timeout, DNS
    const status = err.response.status;
    if (status === 429) return 'rate';
    if (status === 401 || status === 403) return 'auth';
    if (status >= 500) return 'server';
  }
  return 'unknown';
}

// Message de toast honnête pour une erreur d'action (login, recover…). Sur un
// quota, on surface le message EXACT du serveur (les limiteurs disent la vraie
// durée : « 15 minutes » pour le verrou de compte, « quelques minutes » pour le
// plancher) au lieu d'inventer une durée (Gardien + Sènami, baptême du feu).
export function toastMessageForError(err: unknown, fallback: string): string {
  switch (classifyApiError(err)) {
    case 'network':
      return 'Pas de connexion. Vérifie ton réseau et réessaie.';
    case 'server':
      return 'Petit souci de notre côté, réessaie dans un instant.';
    case 'rate': {
      const msg = axios.isAxiosError(err)
        ? (err.response?.data as { error?: string } | undefined)?.error
        : undefined;
      return msg ?? 'Trop de tentatives, réessaie dans un instant.';
    }
    default:
      return fallback;
  }
}

// ─────────────────────────────────────────────
// Axios instance
// ─────────────────────────────────────────────
const baseURL = import.meta.env.VITE_API_URL ?? '/api';

// ─────────────────────────────────────────────
// Photos servies par l'API (`/uploads/…`, express.static dans app.ts). Le
// serveur renvoie un chemin RELATIF ; en dev le proxy Vite `/uploads` le
// résout, mais en prod le front est bâti avec `VITE_API_URL` absolu
// (api-sitest.epitools.bj) et vit sur un autre hôte (sitest.epitools.bj) :
// le chemin relatif tombait sur le nginx statique du front → 404, aucune
// photo de repas n'apparaissait (constat sitest 2026-10-10). On ancre donc
// tout chemin relatif sur l'ORIGINE de l'API, une seule fois, ici.
// ─────────────────────────────────────────────
export function resoudreUrlPhoto(
  chemin: string | null | undefined,
  apiBaseUrl: string = baseURL,
): string | undefined {
  if (!chemin) return undefined;
  if (!chemin.startsWith('/')) return chemin;
  const origine = /^https?:\/\//.test(apiBaseUrl) ? new URL(apiBaseUrl).origin : '';
  return `${origine}${chemin}`;
}

export const api: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 15_000,
});

// Attach Bearer token from authStore
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─────────────────────────────────────────────
// Backend renvoie { success, data } — on unwrap
// pour que les callers reçoivent directement le payload.
// Erreurs : { success: false, error } reste levé tel quel
// par axios (status >= 400 → catch côté caller).
// ─────────────────────────────────────────────
api.interceptors.response.use((res) => {
  const body = res.data;
  if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
    res.data = body.data;
  }
  return res;
});

// ─────────────────────────────────────────────
// 401 → refresh token, retry once. Single-flight par onglet (promesse de
// module) + verrou navigateur entre onglets (navigator.locks, repli direct
// sans l'API — wrap-ready ADR 0007 D3). Révision Frappe 1 (2026-08-30) :
// seule une vraie invalidation serveur (401 du refresh) déconnecte — un
// timeout, une coupure ou un quota n'éjectent plus un utilisateur valide.
// ─────────────────────────────────────────────
let refreshPromise: Promise<string | null> | null = null;
let dernierRefreshOkAt = 0;

async function executerRefresh(): Promise<string | null> {
  try {
    // Backend renvoie { success, data: { accessToken } } — appel axios brut
    // (hors instance `api`), pas d'unwrap automatique de `data`.
    const r = await axios.post(`${baseURL}/auth/refresh`, {}, { withCredentials: true });
    const { accessToken } = validateResponse(refreshResponseSchema, r.data?.data, '/auth/refresh');
    useAuthStore.getState().setAccessToken(accessToken);
    dernierRefreshOkAt = Date.now();
    return accessToken;
  } catch (err) {
    // Ne déconnecte QUE si le serveur dit vraiment que la session est morte.
    if (axios.isAxiosError(err) && err.response?.status === 401) {
      useAuthStore.getState().clear();
    }
    return null;
  }
}

// Exportée pour le bootstrap au montage de App.tsx (ADR 0002 D1) : un
// accessToken en mémoire uniquement ne survit pas à un refresh de page ; ce
// helper relance le même mécanisme single-flight que l'interceptor 401 pour
// éviter un aller-retour 401 systématique sur la première requête.
export async function refreshAccessToken(): Promise<string | null> {
  // Un refresh vient d'aboutir (bootstrap, ou 401 concurrent d'une requête
  // partie avec l'ancien token) : on réutilise le jeton frais au lieu
  // d'imposer une rotation de plus — pendant côté client de l'étranglement
  // de rotation serveur (sessions.service.ts).
  const { accessToken } = useAuthStore.getState();
  if (accessToken && Date.now() - dernierRefreshOkAt < 10_000) return accessToken;

  if (!refreshPromise) {
    // navigator.locks sérialise les refresh ENTRE onglets — la promesse de
    // module ne couvre qu'un seul onglet. Sans l'API (vieux WebView) : repli
    // direct, le serveur tolère de toute façon les refresh concurrents
    // (fenêtre de grâce + étranglement de rotation).
    const execution =
      typeof navigator !== 'undefined' && 'locks' in navigator
        ? navigator.locks.request('myfood-auth-refresh', executerRefresh)
        : executerRefresh();
    refreshPromise = Promise.resolve(execution).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

interface RetriedConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as RetriedConfig | undefined;
    if (!original || original._retry) throw error;

    if (error.response?.status === 401 && !original.url?.includes('/auth/')) {
      original._retry = true;
      const fresh = await refreshAccessToken();
      if (fresh) {
        original.headers = { ...(original.headers ?? {}), Authorization: `Bearer ${fresh}` };
        return api.request(original);
      }
      // Refresh raté : rebond vers /login UNIQUEMENT si la session est
      // réellement morte (clear() exécuté sur le 401 du refresh). Un échec
      // passager (réseau, quota, 5xx) laisse la page afficher son état
      // d'erreur honnête — l'utilisateur reste connecté.
      const sessionMorte = !useAuthStore.getState().user;
      if (
        sessionMorte &&
        typeof window !== 'undefined' &&
        !window.location.pathname.startsWith('/login')
      ) {
        window.location.assign('/login');
      }
    }
    throw error;
  },
);

// ─────────────────────────────────────────────
// Endpoints
// ─────────────────────────────────────────────
export const authApi = {
  login: (p: LoginPayload): Promise<AuthResponse> =>
    api.post('/auth/login', p).then((r) => validateResponse(authResponseSchema, r.data, '/auth/login')),
  register: (p: RegisterPayload): Promise<AuthResponse> =>
    api
      .post('/auth/register', p)
      .then((r) => validateResponse(authResponseSchema, r.data, '/auth/register')),
  // Réponse anti-énumération (ADR 0002 D4) : { success, message } toujours
  // identique, que le compte existe ou non — pas de 'data' à unwrap.
  recover: (p: RecoverPayload) =>
    api
      .post<{ success: boolean; message: string }>('/auth/recover', p)
      .then((r) => r.data),
  logout: () => api.post<void>('/auth/logout').then((r) => r.data),
  pushSubscribe: (subscription: PushSubscriptionJSON) =>
    api.post<void>('/auth/push-subscribe', { subscription }).then((r) => r.data),
  // Frappe 2 : la désactivation doit prévenir le serveur, sinon la base garde
  // une souscription morte vers laquelle il continuerait de pousser.
  pushUnsubscribe: () => api.post<void>('/auth/push-unsubscribe').then((r) => r.data),
  // Appareils connectés (T7, kit v2 §5) — le fil EST le domaine ici (pas
  // d'adaptateur) : type exporté directement en SessionWire (ADR 0005 D3).
  sessions: (): Promise<SessionWire[]> =>
    api
      .get('/auth/sessions')
      .then((r) => validateResponse(sessionsResponseSchema, r.data, '/auth/sessions').sessions),
  // `inclureCourante` par défaut à false : "Se déconnecter partout" épargne
  // l'appareil sur lequel le geste est fait (sinon l'utilisateur se
  // déconnecterait lui-même en cliquant le bouton).
  revokeAllSessions: (inclureCourante = false): Promise<{ revoquees: number }> =>
    api
      .post('/auth/sessions/revoke-all', { inclureCourante })
      .then((r) => validateResponse(revokeAllSessionsResponseSchema, r.data, '/auth/sessions/revoke-all')),
};

// ─────────────────────────────────────────────
// Télémétrie plateforme PWA (ADR 0007 D2 — Métriques A/B du cap natif).
// Fire-and-forget côté appelant (`usePwaTelemetryBoot`, `lib/pwa.ts`) : la
// réponse n'a pas de forme utile à valider, seul le succès/échec HTTP compte.
// ─────────────────────────────────────────────
export const metricsApi = {
  sendPwaTelemetry: (payload: PwaTelemetryPayload): Promise<void> =>
    api.post('/metrics/pwa', payload).then(() => undefined),
};

// ─────────────────────────────────────────────
// Adapter : le backend Prisma renvoie un Menu avec `nomPlat`,
// `photoUrl`, `dateLivraison`. Le frontend Campus utilise
// `name`, `imageUrl`, `delivery`. On normalise ici pour
// ne pas dupliquer le mapping dans chaque page.
//
// Type d'entrée = MenuWire (z.infer de `menuSchema`, packages/shared) — pas
// une interface écrite à la main : le schéma partagé est la source unique
// de la forme du fil (ADR 0005 D3).
// ─────────────────────────────────────────────
function mapBackendMenu(m: MenuWire): Menu {
  const livraison = m.dateLivraison ? new Date(m.dateLivraison) : null;
  const delivery = livraison
    ? livraison.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' }) +
      ' · ' +
      livraison.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : 'Demain · 12h00';

  return {
    id: m.id,
    name: m.nomPlat ?? 'Plat du jour',
    chef: 'Cantine Epitech',
    campus: 'Epitech Cotonou',
    delivery,
    description: m.description ?? '',
    nutrition: [],
    orders: 0,
    color: '#F97316',
    imageUrl: resoudreUrlPhoto(m.photoUrl),
    // ISO brut de dateLivraison (T5) : sert à détecter "déjà commandé" sur
    // l'Accueil en comparant à `OrderHistoryItem.menuDateLivraison`, même
    // colonne source côté Prisma — comparaison directe sans recalcul TZ.
    publishedFor: m.dateLivraison,
    commandeOuverte: m.commande?.ouverte,
    commandeDeadline: m.commande?.deadline,
    commandeRaison: m.commande?.raison,
    // Accroche du drop (design-handoff item 1) — optionnelle, jamais de
    // valeur par défaut : `undefined` si l'admin n'en a pas saisi (rendu à
    // deux états repris en T5).
    accroche: m.accroche ?? undefined,
    accrocheMot: m.accrocheMot ?? undefined,
  };
}

// Champs texte + fichier → FormData (les valeurs numériques deviennent des
// chaînes : c'est le contrat multipart, le serveur les coerce). `undefined`
// est omis, comme le JSON l'aurait fait.
function multipartAvecPhoto(
  champs: Record<string, string | number | undefined>,
  photo: File,
): FormData {
  const fd = new FormData();
  for (const [cle, valeur] of Object.entries(champs)) {
    if (valeur !== undefined) fd.append(cle, String(valeur));
  }
  fd.append('photo', photo);
  return fd;
}

export const menusApi = {
  // Priorité Task 2 (ADR 0005 D3) : validé au bord avant l'adaptateur.
  today: () =>
    api.get('/menus/today').then((r) => mapBackendMenu(validateResponse(menuSchema, r.data, '/menus/today'))),
  // Hors périmètre priorité : cast générique conservé (règle d'avant,
  // schemas.ts), typé sur la même forme MenuWire pour rester cohérent.
  week: () => api.get<MenuWire[]>('/menus/week').then((r) => r.data.map(mapBackendMenu)),
  // Tâche 8 (chantier 5) : le cast générique précédent (`Candidate[]`)
  // mentait sur la forme — le fil réel de GET /menus/candidates est
  // `MenuCandidate` Prisma (nomPlat/photoUrl/totalVotes/elu), pas
  // `{name,chef,tag,votes,color}` : tous les champs affichés étaient
  // `undefined` en pratique. Même schéma + adaptateur que `votesApi` (le fil
  // est identique — `voteCandidateSchema` / `mapBackendCandidate` ci-dessus).
  candidates: () =>
    api
      .get('/menus/candidates')
      .then((r) =>
        validateResponse(z.array(voteCandidateSchema), r.data, '/menus/candidates').map(mapBackendCandidate),
      ),
  // [ADMIN] proposer un candidat au vote — POST /menus/candidates
  // (createCandidateSchema côté API : nomPlat/description?/semaineNumero/
  // annee). Photo optionnelle (Frappe 2, design-handoff items 7/11) : avec un
  // fichier, la requête passe en multipart — le chemin upload.single('photo')
  // du serveur, où multer livre les champs texte en chaînes (numériques
  // coercés par Zod côté API). Sans photo : JSON strictement inchangé.
  createCandidate: ({
    photo,
    ...payload
  }: {
    nomPlat: string;
    description?: string;
    semaineNumero: number;
    annee: number;
    photo?: File;
  }) =>
    api
      .post<VoteCandidateWire>('/menus/candidates', photo ? multipartAvecPhoto(payload, photo) : payload)
      .then((r) => r.data),
  publish: ({
    photo,
    ...payload
  }: {
    nomPlat: string;
    description?: string;
    dateLivraison: string;
    menuCandidateId?: string;
    /** Accroche du drop (design-handoff item 1) — optionnelle. */
    accroche?: string;
    /** Doit être un mot de `accroche` (validation Zod côté API). */
    accrocheMot?: string;
    photo?: File;
  }) =>
    api
      .post<MenuWire>('/menus', photo ? multipartAvecPhoto(payload, photo) : payload)
      .then((r) => mapBackendMenu(r.data)),
};

// Adapter récap commandes du jour (admin, tâche 8) — GET /orders/today a une
// forme DIFFÉRENTE de GET /orders/me (`user` inclus, `stats` agrégées, cf.
// orders.controller.ts getOrdersToday) : mismatch préexistant
// corrigé au passage (le cast générique `Order[]` d'avant donnait
// `o.client`/`o.quantite` toujours `undefined`). Même idiome que
// `mapBackendOrderHistory` : schéma partagé + adaptateur dédié.
function mapBackendOrdersToday(w: OrdersTodayWire): AdminOrdersToday {
  const rows = w.orders.map((o) => ({
    id: o.id,
    clientName: `${o.user?.prenom ?? ''} ${o.user?.nom ?? ''}`.trim() || 'Client',
    menuNom: o.menu?.nomPlat ?? 'Repas',
    statut: o.statut,
    orderedAt: o.orderedAt ?? o.createdAt ?? new Date().toISOString(),
    signalement: o.signalement,
    remisPar: o.deliveredBy?.prenom ?? null,
    sansAmbassade: o.user?.ambassadeId == null,
  }));
  return {
    rows,
    stats: w.stats ?? {
      totalCommandes: rows.length,
      totalRepas: rows.length,
      membresUniques: new Set(w.orders.map((o) => o.userId).filter(Boolean)).size,
    },
  };
}

// Adapter historique commandes (T5) — GET /orders/me alimente l'Accueil
// ("Tes 3 dernières commandes" + détection déjà-commandé), qui a besoin
// d'une forme FIDÈLE au fil réel (statut EN, menu.nomPlat/photoUrl/
// dateLivraison). `Order` (types.ts) documente un décalage préexistant
// (statut français, cf. orderSchema, packages/shared) : plutôt que de forcer ces données dans une forme
// qui les décrit mal, un type minimal dédié. `ordersApi.create/today/
// setStatus` (admin, hors périmètre T5) gardent le cast `Order` existant
// tel quel — aucun changement de comportement sur ces call sites.
function mapBackendOrderHistory(o: OrderWire): OrderHistoryItem {
  return {
    id: o.id,
    statut: o.statut,
    menuNom: o.menu?.nomPlat ?? 'Repas',
    menuPhotoUrl: resoudreUrlPhoto(o.menu?.photoUrl),
    menuDateLivraison: o.menu?.dateLivraison,
    orderedAt: o.orderedAt ?? o.createdAt ?? new Date().toISOString(),
    deliveredByPrenom: o.deliveredBy?.prenom ?? null,
    livreeAt: o.livreeAt ?? null,
    quantite: o.quantite ?? 1,
  };
}

// Forme réelle de la réponse de POST /orders (orders.controller.ts,
// createOrder / rejouerCommande) : la commande créée, sa quantité et le
// solde restant. Seul `order.id` est consommé côté client (lien « Voir ma
// commande » de l'écran de succès) — validé au bord sur ce minimum.
const orderCreatedSchema = z.object({
  order: z.object({ id: z.string() }),
  quantite: z.number().int().optional(),
  creditsRestants: z.number().int().optional(),
});
export type OrderCreated = z.infer<typeof orderCreatedSchema>;

export const ordersApi = {
  // clientRequestId : clé d'idempotence (Frappe 3) — le serveur rejoue la
  // même commande sur un retry au lieu de redébiter.
  create: (payload: { menuId: string; quantite: number; clientRequestId?: string }): Promise<OrderCreated> =>
    api.post('/orders', payload).then((r) => validateResponse(orderCreatedSchema, r.data, 'POST /orders')),
  // Priorité Task 2 (ADR 0005 D3) : validé au bord, puis adapté fidèlement
  // (mapBackendOrderHistory ci-dessus) — révisé en T5.
  me: (): Promise<OrderHistoryItem[]> =>
    api
      .get('/orders/me')
      .then((r) =>
        validateResponse(z.array(orderSchema), r.data, '/orders/me').map(mapBackendOrderHistory),
      ),
  // Le ticket de remise de la personne (un par jour, toutes ses commandes du
  // jour) — décidé serveur : null avant 11h ou sans commande (ADR 0008 révisé).
  ticketDuJour: (): Promise<TicketJourWire | null> =>
    api
      .get('/orders/ticket-du-jour')
      .then((r) => validateResponse(ticketJourSchema.nullable(), r.data, '/orders/ticket-du-jour')),
  // Détail d'une commande (FIX-2, correctifs sept. 2026) : même fil et même
  // adaptateur que /orders/me.
  get: (id: string): Promise<OrderHistoryItem> =>
    api
      .get(`/orders/${id}`)
      .then((r) => mapBackendOrderHistory(validateResponse(orderSchema, r.data, '/orders/:id'))),
  // Tâche 8 (chantier 5) : validé au bord + adapté (mapBackendOrdersToday
  // ci-dessus) — corrige le mismatch de forme préexistant, même traitement
  // que les priorités Task 2.
  today: (): Promise<AdminOrdersToday> =>
    api
      .get('/orders/today')
      .then((r) => mapBackendOrdersToday(validateResponse(ordersTodaySchema, r.data, '/orders/today'))),
  setStatus: (id: string, status: OrderStatus) =>
    api.patch<Order>(`/orders/${id}/deliver`, { status }).then((r) => r.data),
};

// ─────────────────────────────────────────────
// Ambassadeurs (ADR 0008) — validé au bord (ADR 0005 D3), pas d'adaptateur :
// le fil validé EST le domaine côté ambassadeur.
// ─────────────────────────────────────────────
export const ambassadesApi = {
  me: (): Promise<AmbassadeMeWire> =>
    api.get('/ambassades/me').then((r) => validateResponse(ambassadeMeSchema, r.data, '/ambassades/me')),
  membre: (): Promise<MonAmbassadeurWire> =>
    api.get('/ambassades/membre').then((r) => validateResponse(monAmbassadeurSchema, r.data, '/ambassades/membre')),
  absence: (jour: 'AUJOURDHUI' | 'DEMAIN'): Promise<{ absentLe: string }> =>
    api.post('/ambassades/me/absence', { jour }).then((r) => r.data as { absentLe: string }),
  distribution: (): Promise<DistributionWire> =>
    api.get('/ambassades/distribution').then((r) => validateResponse(distributionSchema, r.data, '/ambassades/distribution')),
  remiseQr: (token: string): Promise<RemiseResultWire> =>
    api.post('/ambassades/remise/qr', { token }).then((r) => validateResponse(remiseResultSchema, r.data, '/ambassades/remise/qr')),
  remiseCode: (userId: string, code: string): Promise<RemiseResultWire> =>
    api.post('/ambassades/remise/code', { userId, code }).then((r) => validateResponse(remiseResultSchema, r.data, '/ambassades/remise/code')),
  rejoindre: (code: string): Promise<{ titulaire: string }> =>
    api.post(`/ambassades/rejoindre/${encodeURIComponent(code)}`).then((r) => r.data as { titulaire: string }),
  invitation: (code: string): Promise<InvitationWire> =>
    api.get(`/ambassades/invitation/${encodeURIComponent(code)}`).then((r) => validateResponse(invitationSchema, r.data, '/ambassades/invitation')),
};

/** Lit une erreur de remise (4xx avec `code`/`detail`), sinon code 'RESEAU'. */
export function remiseErreurDe(e: unknown): {
  code: string;
  message: string;
  userId?: string;
  deliveredByPrenom?: string;
  echecsRestants?: number;
  livreeAt?: string | null;
} {
  const err = e as AxiosError<{
    error?: string;
    code?: string;
    detail?: { userId?: string; deliveredByPrenom?: string; echecsRestants?: number; livreeAt?: string | null };
  }>;
  const body = err?.response?.data;
  if (body?.code) {
    return {
      code: body.code,
      message: body.error ?? 'Erreur',
      userId: body.detail?.userId,
      deliveredByPrenom: body.detail?.deliveredByPrenom,
      echecsRestants: body.detail?.echecsRestants,
      livreeAt: body.detail?.livreeAt,
    };
  }
  return { code: 'RESEAU', message: 'Réseau indisponible, réessaie' };
}

// ─────────────────────────────────────────────
// Catalogue tarifaire — seule source de prix côté front (ADR 0001 D6) :
// aucun montant n'est jamais calculé ni codé en dur côté client.
// ─────────────────────────────────────────────
export const formulesApi = {
  // Priorité Task 2 (ADR 0005 D3) : validé au bord, pas d'adaptateur (le fil
  // EST le domaine ici — cf. FormuleInfo = z.infer<typeof formuleInfoSchema>
  // dans types.ts).
  list: () => api.get('/formules').then((r) => validateResponse(z.array(formuleInfoSchema), r.data, '/formules')),
  // Réglages admin tarifs (tâche 8, report chantier 3) — PUT /admin/formules/:id.
  // Même schéma de réponse que list() (formuleInfoSchema, passthrough).
  updateAdmin: (id: Formule, payload: UpdateFormulePayload) =>
    api
      .put(`/admin/formules/${id}`, payload)
      .then((r) => validateResponse(formuleInfoSchema, r.data, `/admin/formules/${id}`)),
};

// ─────────────────────────────────────────────
// Adapter abonnement (backend `statut` FR → frontend `status` EN)
//
// Type d'entrée = SubscriptionWire (z.infer de `subscriptionSchema`,
// packages/shared) — plus d'interface écrite à la main (ADR 0005 D3).
// ─────────────────────────────────────────────
function mapBackendSubscription(s: SubscriptionWire): Subscription {
  return {
    id: s.id,
    userId: s.userId,
    formule: s.formule,
    creditsRestants: s.creditsRestants,
    creditsTotal: s.creditsTotal,
    status: s.statut,
    expiresAt: s.expiresAt ?? undefined,
    startedAt: s.startedAt,
  };
}

export const subscriptionsApi = {
  // Priorité Task 2 : validé au bord avant l'adaptateur.
  // 404 « Aucun abonnement actif » = un VRAI solde nul (formule expirée, compte
  // neuf) → null, le badge dit « 0 crédit ». Les autres échecs (réseau, 500)
  // restent des rejets : le badge dit « — », jamais un 0 fabriqué (revue Sènami).
  me: () =>
    api
      .get('/subscriptions/me')
      .then((r) =>
        r.data ? mapBackendSubscription(validateResponse(subscriptionSchema, r.data, '/subscriptions/me')) : null,
      )
      .catch((e: unknown) => {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404) return null;
        throw e;
      }),
  // Initiation 100% serveur (ADR 0001 D3) : le client ne fournit ni montant ni
  // référence — le serveur calcule le prix, initie le provider et renvoie
  // subscription + payment (PENDING) dans le même appel.
  create: (payload: { formule: Formule; operateur: Operateur; phone: string }) =>
    api.post('/subscriptions', payload).then((r) => {
      const parsed = validateResponse(
        z.object({ subscription: subscriptionSchema, payment: paymentSchema }),
        r.data,
        '/subscriptions',
      );
      return {
        subscription: mapBackendSubscription(parsed.subscription),
        payment: mapBackendPayment(parsed.payment),
      };
    }),
};

// ─────────────────────────────────────────────
// Adapter paiements admin
//
// Type d'entrée = PaymentWire (z.infer de `paymentSchema`, packages/shared)
// — couvre les 3 formes réelles (brute /payments/:id, avec `subscription`
// /payments/me, avec `subscription`+`user` /admin/payments).
// ─────────────────────────────────────────────
function mapBackendPayment(p: PaymentWire): Payment {
  const client = `${p.user?.prenom ?? ''} ${p.user?.nom ?? ''}`.trim() || 'Client';
  const status: Payment['status'] =
    p.statut === 'CONFIRMED' ? 'CONFIRMED' :
    p.statut === 'FAILED' || p.statut === 'EXPIRED' ? 'REJECTED' : 'PENDING';
  // Fuseau métier (ADR 0004 D2), motif formatHeure/formatDeadline
  // (utils.ts) : l'ancien `toLocaleTimeString` sans `timeZone` lisait
  // l'horloge locale du navigateur, pas Africa/Porto-Novo — corrigé au
  // passage (minor tracé, chantier 7 tâche 4).
  const time = (p.createdAt && formatHeure(p.createdAt)) || '—';

  return {
    id: p.id,
    subscriptionId: p.subscriptionId,
    client,
    avatar: '',
    color: pickColor(p.userId ?? p.id),
    formule: p.subscription?.formule ?? 'DAY',
    amount: (p.montant ?? 0).toLocaleString('fr-FR'),
    op: p.operateur ?? 'MTN',
    ref: p.referenceExterne ?? '—',
    time,
    status,
    createdAt: p.createdAt,
  };
}

export const paymentsApi = {
  // declare() supprimé (ADR 0001 D3) : POST /api/payments n'existe plus,
  // l'initiation passe désormais uniquement par subscriptionsApi.create().
  // get() alimente le polling honnête côté client (ADR 0001 D5) : lit
  // l'état en base, ne décrète jamais un succès local.
  // Priorité Task 2 : validé au bord avant l'adaptateur.
  get: (id: string) =>
    api
      .get(`/payments/${id}`)
      .then((r) => mapBackendPayment(validateResponse(paymentSchema, r.data, `/payments/${id}`))),
  // Hors périmètre priorité (non listé côté Task 2) : cast générique conservé.
  confirm: (id: string) =>
    api
      .patch<PaymentWire>(`/payments/${id}/confirm`)
      .then((r) => mapBackendPayment(r.data)),
  // Priorité Task 2 : validé au bord avant l'adaptateur.
  me: () =>
    api
      .get('/payments/me')
      .then((r) => validateResponse(z.array(paymentSchema), r.data, '/payments/me').map(mapBackendPayment)),
  // /payments?status=PENDING n'existe pas cote backend ; on filtre cote frontend
  // a partir de /admin/payments. Conservé pour l'API publique. Hors périmètre
  // priorité (admin non listé côté Task 2, sauf /admin/alerts) : cast conservé.
  pending: () =>
    api
      .get<PaymentWire[]>('/admin/payments')
      .then((r) => r.data.filter((p) => p.statut === 'PENDING').map(mapBackendPayment)),
  all: () =>
    api
      .get<PaymentWire[]>('/admin/payments')
      .then((r) => r.data.map(mapBackendPayment)),
};

// ─────────────────────────────────────────────
// Adapter votes : backend renvoie nomPlat/photoUrl/totalVotes
// + window/votesRestants. Frontend utilise name/imageUrl/votes
// + windowOpensAt/closesAt/state.
//
// Types d'entrée = VoteCandidateWire / VoteStateWire (z.infer de
// `voteCandidateSchema` / `voteStateSchema`, packages/shared) — ADR 0005 D3.
// ─────────────────────────────────────────────
const FALLBACK_CANDIDATE_COLORS = [
  '#F97316', '#FBBF24', '#B8451B', '#E85D04',
  '#C2410C', '#FB923C', '#D97706', '#92400E',
  '#A16207', '#7C2D12',
];

function mapBackendCandidate(c: VoteCandidateWire, idx: number): Candidate {
  return {
    id: c.id,
    name: c.nomPlat ?? 'Plat',
    chef: 'Cantine Epitech',
    tag: c.elu ? 'Élu' : 'Tradi',
    votes: c.totalVotes ?? 0,
    color: FALLBACK_CANDIDATE_COLORS[idx % FALLBACK_CANDIDATE_COLORS.length],
    imageUrl: resoudreUrlPhoto(c.photoUrl),
    elu: c.elu ?? false,
    // Tâche 8 (admin Candidates.tsx) : permet de pré-remplir le formulaire
    // "+ Nouveau candidat" sur la même semaine que la liste affichée, sans
    // jamais recalculer une semaine métier côté client (interdit ADR 0004 D2).
    semaineNumero: c.semaineNumero,
    annee: c.annee,
  };
}

function mapBackendVoteState(b: VoteStateWire): VoteState {
  const w = b.window;
  const state: VoteState['state'] = w?.isOpen ? 'open' : 'closed';
  return {
    candidates: (b.candidates ?? []).map(mapBackendCandidate),
    myVotes: b.myVotes ?? [],
    votesRemaining: b.votesRestants ?? (b.maxVotes ?? 5) - (b.myVotes?.length ?? 0),
    maxVotes: b.maxVotes ?? 5,
    windowOpensAt: w?.opensAt ?? '',
    windowClosesAt: w?.closesAt ?? '',
    state,
    semaineNumero: b.semaineNumero,
    annee: b.annee,
  };
}

export const votesApi = {
  // Priorité Task 2 : validé au bord avant l'adaptateur.
  current: () =>
    api
      .get('/votes/current')
      .then((r) => mapBackendVoteState(validateResponse(voteStateSchema, r.data, '/votes/current'))),
  // Hors périmètre priorité (seul /votes/current est listé) : cast conservé,
  // même forme VoteStateWire pour rester cohérent.
  vote: (candidateId: string) =>
    api
      .post<VoteStateWire>('/votes', { candidateId })
      .then((r) => mapBackendVoteState(r.data)),
  results: (week: string) =>
    api
      .get<VoteStateWire>(`/votes/results/${week}`)
      .then((r) => mapBackendVoteState(r.data)),
};

// ─────────────────────────────────────────────
// Adapters admin : backend renvoie le shape Prisma
// (nom/prenom/scoreEngagement/niveauRisque/...)
// alors que le frontend Campus utilise un shape
// flat (name/score/risk/...). On normalise ici.
// Tâche 8 : le type d'entrée est désormais `AdminClientWire` (z.infer de
// `adminClientSchema`, packages/shared) — plus d'interface écrite à la main
// pour cette forme (ADR 0005 D3), même doctrine que les autres adaptateurs.
// ─────────────────────────────────────────────
const ADMIN_COLORS = ['#F97316', '#FBBF24', '#B8451B', '#E85D04', '#C2410C', '#FB923C', '#D97706', '#92400E'];
// Exportée : réutilisée par ClientDetail.tsx (bannière fiche client) pour
// garder la même identité visuelle stable qu'un client sur la liste.
export function pickColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return ADMIN_COLORS[Math.abs(hash) % ADMIN_COLORS.length];
}

// Exportée : même repli LOW/MEDIUM/HIGH/CRITICAL → low/mid/high réutilisé par
// ClientDetail.tsx (fiche, forme nichée `adminClientDetailSchema`) — source
// unique pour ne pas diverger de la liste ci-dessous.
export function mapNiveauRisque(n: string | null | undefined): AdminClient['risk'] {
  return n === 'LOW' ? 'low' : n === 'MEDIUM' ? 'mid' : 'high';
}

function mapBackendAdminClient(c: AdminClientWire): AdminClient {
  const fullName = `${c.prenom ?? ''} ${c.nom ?? ''}`.trim() || 'Client';
  const score = Math.round(c.scoreEngagement ?? 0);
  const risk = mapNiveauRisque(c.niveauRisque);
  const lastDays = c.jourDepuisDerniereCmd ?? null;
  const last =
    lastDays === null || lastDays === undefined ? '—' :
    lastDays === 0 ? 'Aujourd\'hui' :
    lastDays === 1 ? 'Hier' : `Il y a ${lastDays}j`;
  // `formule` alimente un enum affiché en UI : validée via la primitive
  // partagée `formuleSchema` plutôt qu'un cast `as` aveugle (ADR 0005 D3,
  // cible explicite des 4 casts `as` d'adaptateurs) — `adminClientSchema`
  // ci-dessus ne resserre pas ce champ à l'énum pour rester conservateur/
  // passthrough (D2 : mieux vaut un fallback ici qu'un rejet de schéma).
  const formuleParsed = formuleSchema.safeParse(c.formuleActive);
  const formule: AdminClient['formule'] = formuleParsed.success ? formuleParsed.data : 'DAY';

  return {
    id: c.id,
    name: fullName,
    promo: c.profil === 'STUDENT_EPITECH' ? 'Epitech' : 'Autre',
    formule,
    credits: c.creditsRestants ?? 0,
    score,
    risk,
    last,
    orders: 0,
    orderRate: Math.round((c.tauxCommande ?? 0) * 100),
    channel: c.canalAcquisition ?? '—',
    color: pickColor(c.id),
    email: c.email ?? undefined,
    telephone: c.telephone,
    lastDays: lastDays ?? undefined,
  };
}

// Exportée : réutilisée par ClientDetail.tsx (alertes réelles de la fiche
// client, `adminClientDetailSchema.alerts`) — même libellés que la liste.
export const ALERT_LABELS: Record<string, string> = {
  INACTIVE_7_DAYS: 'Inactif 7 jours',
  CREDITS_EXPIRED_NO_RENEWAL: 'Crédits expirés',
  ORDER_RATE_DROP: 'Baisse commandes',
  NEVER_VOTED: 'N\'a jamais voté',
  LOW_ENGAGEMENT_SCORE: 'Score critique',
  SUR_PAIEMENT: 'Sur-paiement à rembourser',
  DEMANDE_RECUPERATION: 'Demande de récupération de compte',
  REUTILISATION_SESSION: 'Alerte sécurité : session réutilisée',
};

function mapBackendAdminAlert(a: AdminAlertWire): AdminAlert {
  const fullName = `${a.user?.prenom ?? ''} ${a.user?.nom ?? ''}`.trim() || 'Client';
  return {
    id: a.id,
    priority: a.priorite,
    name: fullName,
    avatar: '',
    color: pickColor(a.userId),
    msg: ALERT_LABELS[a.typeAlerte] ?? a.typeAlerte,
    detail: a.message,
    resolved: a.resolue,
  };
}

// ─────────────────────────────────────────────
// Adapter metrics overview : backend renvoie nbClientsActifs/
// revenusTotal/tauxCommandeGlobal/... ; le frontend Campus
// utilise activeClients/monthRevenue formate/orderRate (0-100)/
// atRisk/voteRate/mrr formate/arpu formate/pendingPayments.
// ─────────────────────────────────────────────
interface BackendMetricsOverview {
  nbClientsActifs?: number;
  revenusTotal?: number;
  tauxCommandeGlobal?: number;
  nbAlertesCritiques?: number;
  tauxVoteGlobal?: number;
  mrr?: number;
  arpu?: number;
  nbPaiementsEnAttente?: number;
  nbCommandesAujourdhui?: number;
}

/** "1234567" → "1.23M", "187000" → "187K", "234" → "234" */
function compactNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return Math.round(n).toLocaleString('fr-FR');
}

function mapBackendMetrics(b: BackendMetricsOverview): MetricsOverview {
  return {
    activeClients: b.nbClientsActifs ?? 0,
    monthRevenue: compactNumber(b.revenusTotal ?? 0),
    orderRate: Math.round((b.tauxCommandeGlobal ?? 0) * 100),
    atRisk: b.nbAlertesCritiques ?? 0,
    voteRate: Math.round((b.tauxVoteGlobal ?? 0) * 100),
    mrr: compactNumber(b.mrr ?? 0),
    arpu: compactNumber(b.arpu ?? 0),
    pendingPayments: b.nbPaiementsEnAttente ?? 0,
    deltas: {},
  };
}

export const adminApi = {
  // Tâche 8 (chantier 5) : validé au bord (adminClientSchema, conservateur/
  // passthrough) avant l'adaptateur — même priorité que les endpoints
  // argent/identité (D3), la liste alimente directement des décisions admin.
  clients: (filter: 'all' | 'at_risk' | 'active' | 'inactive' = 'all') =>
    api
      .get(`/admin/clients?filter=${filter}`)
      .then((r) =>
        validateResponse(z.array(adminClientSchema), r.data, '/admin/clients').map(mapBackendAdminClient),
      ),
  // Tâche 8 : forme NICHÉE différente de la liste (adminClientDetailSchema)
  // — corrige un mismatch préexistant (`mapBackendAdminClient` était
  // appliquée à tort sur cette réponse, produisant des champs `undefined`
  // partout : `formuleActive`/`scoreEngagement`/... n'existent pas à plat
  // sur /admin/clients/:id). Pas d'adaptateur : le fil validé EST le domaine
  // (ADR 0005 D3), la page lit `clientScore`/`subscriptions`/`orders`/
  // `payments`/`alerts` directement.
  client: (id: string): Promise<AdminClientDetail> =>
    api
      .get(`/admin/clients/${id}`)
      .then((r) => validateResponse(adminClientDetailSchema, r.data, `/admin/clients/${id}`)),
  // Priorité Task 2 (admin : alertes uniquement) : validé au bord avant l'adaptateur.
  alerts: () =>
    api
      .get('/admin/alerts')
      .then((r) => validateResponse(z.array(adminAlertSchema), r.data, '/admin/alerts').map(mapBackendAdminAlert)),
  resolveAlert: (id: string) =>
    api.patch<unknown>(`/admin/alerts/${id}/resolve`).then((r) => r.data),
  // Notification manuelle (ClientDetail, tâche 8) — POST /admin/clients/:id/notify.
  notifyClient: (id: string, payload: { title: string; body: string }) =>
    api.post<{ message: string }>(`/admin/clients/${id}/notify`, payload).then((r) => r.data),
  // Remboursement (ClientDetail, tâche 8) — annule l'abonnement ACTIVE et
  // rembourse les crédits restants au prorata (calcul 100% serveur, ADR
  // 0001 D6/D12) : le client ne fournit que la raison.
  refundSubscription: (subscriptionId: string, payload: { raison: string }) =>
    api.put(`/admin/subscriptions/${subscriptionId}/refund`, payload).then((r) => r.data),
  metrics: () =>
    api
      .get<BackendMetricsOverview>('/admin/metrics/overview')
      .then((r) => mapBackendMetrics(r.data)),
  weeklyMetrics: () => api.get('/admin/metrics/weekly').then((r) => r.data),
  acquisitionMetrics: () => api.get('/admin/metrics/acquisition').then((r) => r.data),
  formulesMetrics: () => api.get('/admin/metrics/formules').then((r) => r.data),
  // Tableau de bord Installation PWA (baseline ADR 0007 D2) — validé au bord
  // (agrégats seulement, cf. pwaMetricsSchema) : mêmes garanties que les
  // autres endpoints admin/metrics/*.
  pwaMetrics: (): Promise<PwaMetricsWire> =>
    api
      .get('/admin/metrics/pwa')
      .then((r) => validateResponse(pwaMetricsSchema, r.data, '/admin/metrics/pwa')),
  // Réglages (onglet admin « Réglages », tâche 8, report chantier 2) —
  // GET/PUT /admin/settings(/deadline), validés au bord (adminSettingsSchema).
  settings: () =>
    api.get('/admin/settings').then((r) => {
      const d = validateResponse(adminSettingsSchema, r.data, '/admin/settings');
      return { orderDeadline: d.orderDeadline, remunerationParRepas: d.remunerationParRepas ?? null };
    }),
  updateDeadline: (rule: OrderDeadlineRule) =>
    api
      .put('/admin/settings/deadline', rule)
      .then((r) => validateResponse(adminSettingsSchema, r.data, '/admin/settings/deadline')),
  // Ambassadeurs (ADR 0008) — validé au bord, pas d'adaptateur.
  ambassades: (): Promise<AdminAmbassadeWire[]> =>
    api.get('/admin/ambassades').then((r) => validateResponse(z.array(adminAmbassadeSchema), r.data, '/admin/ambassades')),
  nommerAmbassadeur: (userId: string) => api.post('/admin/ambassades', { userId }).then((r) => r.data),
  patchAmbassade: (id: string, action: 'FERMER' | 'ROUVRIR' | 'REGENERER_CODE') =>
    api.patch(`/admin/ambassades/${id}`, { action }).then((r) => r.data),
  affecterClient: (clientId: string, ambassadeId: string | null) =>
    api.patch(`/admin/clients/${clientId}/ambassade`, { ambassadeId }).then((r) => r.data),
  paie: (semaine?: string): Promise<PaieSemaineWire> =>
    api.get('/admin/ambassades/paie', { params: semaine ? { semaine } : undefined })
      .then((r) => validateResponse(paieSemaineSchema, r.data, '/admin/ambassades/paie')),
  marquerPaye: (ambassadeurId: string, semaine: string) =>
    api.post('/admin/ambassades/paie', { ambassadeurId, semaine }).then((r) => r.data),
  deverrouillerCode: (userId: string) => api.patch(`/admin/tickets-jour/${userId}/deverrouiller`).then((r) => r.data),
  updateRemuneration: (montant: number) => api.put('/admin/settings/remuneration', { montant }).then((r) => r.data),
};
